import { Component, ViewChild, OnInit, NgZone } from '@angular/core';
import { NavController, Platform, AlertController, IonRouterOutlet, ModalController } from '@ionic/angular';
import { Router, RouterEvent, NavigationEnd, Event } from '@angular/router';
import { filter } from 'rxjs/operators';
import { addIcons } from 'ionicons';

import { App, URLOpenListenerEvent } from '@capacitor/app';

import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { TranslateService } from '@ngx-translate/core';

// declare var zip:any;
import { appVersionInfo, appVersionResponse } from '@interfaces/version.interface';

import { DataService } from '@providers/common/data.service';
import { Events } from '@providers/common/events.service';
import { URLService } from '@providers/common/url.service';
import { CapacitorUtilsService } from '@providers/common/capacitorUtils.service';

import { UserProvider } from '@providers/user/user.service';
import { LoginProvider } from '@providers/login/login.service';
import { SessionProvider } from '@providers/session/session.service';
import { AppStorageProvider, set } from '@providers/common/app-storage.service';
import { AppVersionCheckService } from '@providers/common/appVersionCheck.service';
import { FavoriteHomeProvider } from '@providers/favorite-home/favorite-home-service.service';
import { TranslateConfigService } from '@providers/common/translate-config.service';
import { UtilityFunctionService } from '@providers/common/utilityFunction.service';

import { SplashScreenModal } from '@components/modal-splash-screen/modal-splash-screen.component';

import { Location } from '@angular/common';

// Import DevExtreme License key file.
import config from 'devextreme/core/config';
import { licenseKey } from '../devextreme-license';
import { ChatService } from '@providers/chat/chat.service';
import { QuantronAccessInfo } from '@interfaces/Quantron/chat-quantron.interface';
import { UserProfile } from '@interfaces/user.interface';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false,
})
export class CsiApp implements OnInit {
    @ViewChild(IonRouterOutlet, { static: true })
    routerOutlet: IonRouterOutlet = {} as IonRouterOutlet;
    backButtonSubscription: any;

    previousUrl: string = '';
    currentUrl: string = '';
    rootPage: any;

    showChatAIPopup: boolean = false;

    goToRating = '';
    ratingsParam = '';
    isAuth: boolean = false;

    showedAlert: boolean = false;
    confirmAlert: any;
    public activeMenuItem: string = "";
    public civarMenu: any[] = [];
    public quantumCivarMenu: any[] = [];
    showMenuLatDesktop: boolean = true;

    isOutSplashScreen: boolean = true;

    appStoreName: string = '';

    chatQuantronAccessInfo: QuantronAccessInfo = new QuantronAccessInfo();

    /* El widget de Quantron es un script de terceros que se inyecta en el DOM con su
       propio z-index (hasta el máximo permitido por CSS), por lo que ningún z-index
       nuestro puede garantizar quedar por encima. En su lugar, se oculta mientras haya
       al menos un modal abierto en la app y se restaura cuando se cierra el último. */
    private openModalCount = 0;

    constructor(
        private router: Router, private dataService: DataService,
        private platform: Platform, private urlService: URLService,
        private sessionProvider: SessionProvider, private loginProvider: LoginProvider,
        private userProvider: UserProvider, private storage: AppStorageProvider,
        public alertCtrl: AlertController, private events: Events,
        private modalCtrl: ModalController, private translate: TranslateService,
        private versionCheckServ: AppVersionCheckService, private favHomeServ: FavoriteHomeProvider,
        private utilityService: UtilityFunctionService, private location: Location,
        private zone: NgZone, private capacitorService: CapacitorUtilsService,
        private translateConfigServ: TranslateConfigService, private chatAIServ: ChatService,
        private nav: NavController,
    ) {
        try {
            this.initializeApp();
        }
        catch (e) {
            this.msgError(e);
        }
    }

    initializeApp() {
        console.log("App initialize!");
        this.translate.addLangs(['es', 'en', 'jp']);

        this.platform.ready().then(async () => {
            this.events.subscribe("app:logout", (val) => {
                // Logout without promt (sesion expirada)
                // if (!CapacitorUtilsService.isApp) {
                this.unloadQuantronWidget();
                // }
                this.goToLoginPage();
                this.sessionProvider.resetAll();
            });
            this.events.subscribe("app:login", () => {
                this.isAuth = true;
                this.getCivarMenu();
            });
            this.events.subscribe("menulat:togglevisibility", () => {
                this.showMenuLatDesktop = !this.showMenuLatDesktop;
            });

            this.events.subscribe('quantron:enabled', async (status: boolean) => {
                // if (status && !CapacitorUtilsService.isApp) {
                if (status) {
                    const storedInfo = await this.storage.get<QuantronAccessInfo>('quantron:enabled');
                    if (storedInfo?.Widget_Config_Script_Url) {
                        this.chatQuantronAccessInfo = storedInfo;
                    }
                    this.loadQuantronWidget();
                }
            });
            /* this.router.events.subscribe((event: RouterEvent) => {
              let currentRoute = this.router.url;
              this.getActiveMenuItem(currentRoute);
              this.events.publish("nav:active", this.activeMenuItem);
            }); */

            /* this.router.events.pipe(filter(event => event instanceof RouterEvent)).subscribe((event: RouterEvent) => {
              console.log('Simple RouterEvent 1st: ', event.url);
      
              let currentRoute = this.router.url;
              this.getActiveMenuItem(currentRoute);
              this.events.publish("nav:active", this.activeMenuItem);
            }); */

            this.router.events.pipe(
                filter((event: Event | RouterEvent) => event instanceof NavigationEnd) // or RouterEvent, or whatnot
            ).subscribe((event: NavigationEnd) => {
                // console.log('Simple NavigationEnd 2nd: ', event.url);

                let currentRoute = this.router.url;
                this.getActiveMenuItem(currentRoute);
                this.events.publish("nav:active", this.activeMenuItem);
            });

            if (this.platform.is('cordova')) {
                this.isOutSplashScreen = false;

                if (this.platform.is('ios')) {
                    await StatusBar.setStyle({ style: Style.Dark });
                }

                if (this.platform.is('android') || this.platform.is('ios')) {
                    await SplashScreen.hide();

                    const modal = await this.modalCtrl.create({
                        component: SplashScreenModal,
                        cssClass: 'fullscreen',
                        mode: 'md'
                    });
                    modal.onDidDismiss().then(() => {
                        this.isOutSplashScreen = true;
                    });
                    return await modal.present();
                }

                /* if (this.platform.is('android')) {
                  this.androidPermissions.requestPermissions([
                    this.androidPermissions.PERMISSION.READ_EXTERNAL_STORAGE,
                    this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE
                  ]);
                } */

            }

            // let expirationJob = () => {
            //   this.rootPage = LoginPage;
            // }

            // this.sessionProvider.expirationNotifier.subscribe(expirationJob);

        });

        this.backButtonSubscription = this.platform.backButton.subscribe(() => {
            const stackCtrl = (this.routerOutlet as any).stackCtrl;
            const stackId = this.routerOutlet.getActiveStackId();
            const depth = stackCtrl.getStack(stackId).length;
            if (depth == 1) {
                if (!this.showedAlert) {
                    this.confirmExitApp();
                } else {
                    this.showedAlert = false;
                    this.confirmAlert.dismiss();
                }
            }

            this.nav.pop();
        });

        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            this.zone.run(() => {
                const codeToken = event.url.split("redirect?code=").pop();
                const logoutURI = event.url.split("dev.quantum.csigroup.com.mx").pop() || '';
                console.log('--> URL: ', codeToken);
                console.log('--> logoutURI: ', logoutURI);

                if (codeToken) {

                    const routeParams = { // Se genera un objeto con los parametros a transmitir a la siguiente route
                        routeId: this.utilityService.randomNum(),
                        code: codeToken,
                    };

                    this.capacitorService.goToCloseLaunchBrowser();

                    this.dataService.setData(routeParams.routeId, routeParams); // Con ayuda del provider DataService asignamos el objeto parametros a un iD, para de esta manera poder recibirlo en la siguiente ruta/pantalla
                    this.router.navigate(['/login/redirect/' + routeParams.routeId], { replaceUrl: true });
                }

                // App version oAuth logout redirect catch function
                if (logoutURI.includes('/login/logout')) {
                    console.log('logout redirect call')
                    this.sessionProvider.resetAll();

                    setTimeout(() => {
                        this.goToLoginPage();
                        // this.router.navigate(['/login'], { replaceUrl: true });
                    }, 1500);
                }
            });
        });

        // Web version oAuth logout redirect catch function

        let currentPath: string = this.location.path();

        if (currentPath.includes('/login/logout')) {
            console.log('logout redirect call')
            this.sessionProvider.resetAll();

            setTimeout(() => {
                this.goToLoginPage();
                // this.router.navigate(['/login'], { replaceUrl: true });
            }, 1500);
        }

        addIcons({
        });

        // Funcion para asiganar la key de libreria de DevExtreme.
        config({ licenseKey });

        // Funcion para determinar y guardar el tema en base al sistema del usuario.
        this.loadThemeApp();
        // Funcion para determinar y guardar el idioma seleccionado por defecto del usuario.
        this.loadLanguageApp();

        // Funcion para inicializar y asignar el canal para usar el componente de Chat AI.
        this.loadChatAI();
    }

    async loadQuantronWidget() {
        if (!this.chatQuantronAccessInfo.Widget_Config_Script_Url || !this.chatQuantronAccessInfo.Widget_Main_Script_Url) {
            console.warn('loadQuantronWidget: missing script URL —', {
                configUrl: this.chatQuantronAccessInfo.Widget_Config_Script_Url || 'MISSING',
                mainUrl: this.chatQuantronAccessInfo.Widget_Main_Script_Url || 'MISSING',
            });
            return;
        }

        const userInfo = await this.userProvider.getProfile().catch(() => null);

        if (!userInfo?.email || !this.sessionProvider.key) {
            console.warn('loadQuantronWidget: missing required data —', {
                userEmail: userInfo?.email ? 'OK' : 'MISSING',
                sessionKey: this.sessionProvider.key ? 'OK' : 'MISSING',
            });
            return;
        }

        (window as any).QUANTRON_CONFIG = {
            quantumToken: this.sessionProvider.key,
            userEmail: userInfo.email,
        };

        await this.utilityService.loadScript(this.chatQuantronAccessInfo.Widget_Config_Script_Url, 'script');
        await this.utilityService.loadScript(this.chatQuantronAccessInfo.Widget_Main_Script_Url, 'script');

        // this.updateDoomChatClasses();
    }

    unloadQuantronWidget(): void {
        if (!this.chatQuantronAccessInfo.Widget_Config_Script_Url || !this.chatQuantronAccessInfo.Widget_Main_Script_Url) {
            return;
        }

        document.getElementById('qtn-root-host')?.remove();

        this.utilityService.removeScript(this.chatQuantronAccessInfo.Widget_Config_Script_Url, 'script');
        this.utilityService.removeScript(this.chatQuantronAccessInfo.Widget_Main_Script_Url, 'script');

        delete (window as any).QUANTRON_CONFIG;
    }

    async loadLanguageApp() {
        this.translateConfigServ.getDefaultLanguage();
        let languageStr = this.translateConfigServ.getCurrentLang();

        // languageStr = this.utilityService.isEmpty(languageStr) ? 'es' : languageStr;
        console.log('loadLanguageApp', languageStr);

        this.translate.setDefaultLang(languageStr);
        this.translate.use(languageStr);
    }

    async loadThemeApp() {

        const isEnabled = await this.storage.get<boolean>('themeDark:active');
        console.log('Storage, themeDark:active: ', isEnabled);

        if (!this.utilityService.isEmpty(isEnabled)) {

            this.events.publish('themeDark:active', isEnabled);
            this.storage.put('themeDark:active', isEnabled);

            document.body.classList.toggle('dark', isEnabled);
        } else {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            console.log('System, themeDark:active: ', prefersDark);


            this.events.publish('themeDark:active', prefersDark);
            this.storage.put('themeDark:active', prefersDark);

            document.body.classList.toggle('dark', prefersDark);
        }
    }

    loadChatAI() {
        this.events.subscribe('chatAI:active', (state: boolean) => {
            console.log('chatAI is open: ', state);

            this.showChatAIPopup = state;
            /* if (state) {
              this.chatAIServ.openChatPopup();
            } else {
              this.chatAIServ.closeChatAIPopup();
            } */
        });
    }

    async onLoadValidateVersionApp() {
        if (CapacitorUtilsService.isApp) {
            this.appStoreName = CapacitorUtilsService.wichPlatform === 'ios' ? 'App Store' : 'Play Store';

            let appInfo: appVersionInfo = await this.versionCheckServ.getObjectVersionInfo();
            console.log(appInfo);

            this.loginProvider.versionSupported(appInfo).then((response: any) => {
                let result: appVersionResponse = response;

                console.log('Response: ', result);
                if (!result.SupportedVersion) {

                    setTimeout(() => {
                        this.alertCtrl.dismiss();
                        this.updateAppRequired();
                    }, 4000);
                }
            });
        }
    }

    ngOnInit() {
        document.addEventListener('ionModalWillPresent', () => {
            this.openModalCount++;
            this.setQuantronWidgetVisible(false);
        });

        document.addEventListener('ionModalWillDismiss', () => {
            this.openModalCount = Math.max(0, this.openModalCount - 1);
            if (this.openModalCount === 0) this.setQuantronWidgetVisible(true);
        });

        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.previousUrl = this.currentUrl;
            this.urlService.setPreviousUrl(this.previousUrl);
        });

        this.events.subscribe("homePage:Custom", (idPage: number) => {
            // console.log('Redirect page id: ', idPage);
            this.onTypeEmitted(idPage);
        });

        this.sessionProvider.load().then(() => {
            if (this.sessionProvider.isAuthenticated()) {
                this.userProvider.getMenu().then((data: any) => {
                    if (data.ErrorMessage != '') {
                        console.log('Error existe ')
                        setTimeout(() => {
                            // console.log('alert: ', JSON.stringify(this.alertCtrl.getTop()));

                            this.alertCtrl.dismiss();
                        }, 5000);
                    }

                    if (data) {
                        this.events.publish('menuLoad', new Date());
                        if (data.UserProfile != 1 && data.UserProfile) {
                            this.isAuth = true;
                        } else if (data.Preferencias) {
                            this.isAuth = true;

                            if (data.Quantron) {
                                console.log('Quantron full data:', JSON.stringify(data.Quantron));
                                this.chatQuantronAccessInfo = data.Quantron;

                                let isQuantronEnabled: boolean = this.chatQuantronAccessInfo.access && !this.chatQuantronAccessInfo.isWidgetEnable;

                                this.storage.put('quantron:enabled', this.chatQuantronAccessInfo);
                                this.events.publish('quantron:enabled', this.chatQuantronAccessInfo.access);
                                this.events.publish('quantronold:enabled', isQuantronEnabled);
                            }
                            // this.router.navigate(['/home'], { replaceUrl: true });
                        } else {
                            this.goToLoginPage();
                        }


                    }
                    // else {
                    //   this.rootPage = LoginPage;
                    // }
                });
                this.getCivarMenu();
                console.log('---> favHome: ', this.favHomeServ.getFavorite());
            } else {
                this.goToLoginPage();
            }
        });
    }

    async goToLoginPage() {
        this.isAuth = false;

        let currentPath: string = this.location.path();
        if (currentPath.includes('/login/redirect?code=')) {
            let userToken = currentPath.replace('/login/redirect?code=', '');

            const routeParams = { // Se genera un objeto con los parametros a transmitir a la siguiente route
                routeId: this.utilityService.randomNum(),
                code: userToken,
            };
            // console.log('params: ', routeParams);

            this.dataService.setData(routeParams.routeId, routeParams); // Con ayuda del provider DataService asignamos el objeto parametros a un iD, para de esta manera poder recibirlo en la siguiente ruta/pantalla
            this.router.navigate(['/login/redirect/' + routeParams.routeId], { replaceUrl: true });

        } else {
            this.router.navigate(['/login'], { replaceUrl: true });
        }
    }

    ionViewDidEnter() {
        /* this.router.events.subscribe((event: RouterEvent) => {
          let currentRoute = this.router.url;
          this.getActiveMenuItem(currentRoute);
          this.events.publish("nav:active", this.activeMenuItem);
        }); */
    }

    ngAfterViewInit() {
        // this.onLoadValidateVersionApp();
    }

    ngOnDestroy() {
        this.backButtonSubscription.unsubscribe();
    }

    getCivarMenu() {
        this.userProvider.getCivarMenu().then((data: any) => {
            this.civarMenu = data;
            this.events.publish("civarMenu", data);
        }).catch(error => {
            console.error(error);
        });

        this.userProvider.getQuantumCivarMenu().then((data: any) => {
            this.quantumCivarMenu = data;
            this.events.publish("quantumCivarMenu", data);
        }).catch(error => {
            console.error(error);
        });


    }

    async confirmExitApp() {
        this.showedAlert = true;
        const confirmAlert = await this.alertCtrl.create({
            header: 'Salir',
            message: '¿ Esta seguro que desea salir de la aplicación ?',
            buttons: [
                {
                    text: 'Cancelar',
                    handler: () => {
                        this.showedAlert = false;
                        return;
                    }
                },
                {
                    text: 'Aceptar',
                    handler: () => {
                        // navigator['app'].exitApp();
                    }
                }
            ],
            cssClass: 'base-alert'
        });
        await confirmAlert.present();
    }

    async msgError(error: any) {
        this.showedAlert = true;
        const confirmAlert = await this.alertCtrl.create({
            header: 'Prueba',
            message: error,
            buttons: [
                {
                    text: 'Cancelar',
                    handler: () => {
                        this.showedAlert = false;
                        return;
                    }
                },
                {
                    text: 'Aceptar',
                    handler: () => {
                        // navigator['app'].exitApp();
                    }
                }
            ],
            cssClass: 'base-alert'
        });
        await confirmAlert.present();
    }

    private setQuantronWidgetVisible(visible: boolean): void {
        const widgetHost = document.getElementById('qtn-root-host');
        if (widgetHost) widgetHost.style.display = visible ? '' : 'none';
    }

    updateDoomChatClasses() {
        const innerElement = document.getElementById('qtn-root-host')?.shadowRoot?.querySelector('.qtn-window') as HTMLElement | null;
        const innerExpandedElement = document.getElementById('qtn-root-host')?.shadowRoot?.querySelector('.qtn-mode--expanded') as HTMLElement | null;

        const viewportWidth: number = window.innerWidth;
        console.log(`Viewport width: ${viewportWidth}px`);

        const physicalScreenWidth: number = window.screen.width;
        console.log(`Monitor width: ${physicalScreenWidth}px`);

        if (innerElement && (viewportWidth < 768)) {
            innerElement.style.paddingBottom = '15px'; // Modify styling
            innerElement.style.setProperty("top", "100px", "important"); // Modify styling
            innerElement.style.setProperty("height", "calc(100vh - 100px)", "important"); // Modify styling
        }

        if (innerExpandedElement && (viewportWidth >= 768)) {
            innerExpandedElement.style.setProperty("top", "100px", "important"); // Modify styling
            innerExpandedElement.style.setProperty("height", "calc(100vh - 100px)", "important"); // Modify styling
        }
    }

    validateChatIsOpen() {
        const btnElement = document.getElementById('qtn-root-host')?.shadowRoot?.querySelector('#qtn-close-btn') as HTMLButtonElement | null;
        const innerElement = document.getElementById('qtn-root-host')?.shadowRoot?.querySelector('.qtn-app') as HTMLElement | null;

        if (innerElement && btnElement) {
            // TypeScript safely knows innerElement exists here
            btnElement.click(); //Simulate the click in the close  btn.
            // innerElement.classList.remove("qtn-mode--widget");
            // innerElement.classList.add("qtn-mode--closed");
        }
    }

    onTypeEmitted(navControl: any) {
        switch (navControl) {
            case -1:
                // this.goToHome("financiero");
                this.goToDashboardFinanciero();
                break;
            case -2:
                // this.goToHome("operativo");
                this.goToDashboardOperativo();
                break;
            case -3:
                this.goToDashboardAMDF();
                break;
            case -4:
                this.goToDashboardMazda();
                break;
            case -5:
                this.goToDashboardBMW();
                break;
            case -10:
                this.goToDashboardBSCCPVPage();
                break;
            case -11:
                this.goToDashboardBSCCSIPage();
                break;
            case -12:
                this.goToDashboardBSCCSIDPage();
                break;
            case -13:
                this.goToDashboardBSCCSIXPage();
                break;
            case -14:
                this.goToDashboardBSCSunusPage();
                break;
            case 1:
                this.goToHome();
                break;
            case 2:
                this.goToTrackingMap();
                break;
            case 3:
                this.goToTravelList();
                break;
            case 4:
                this.goToScore();
                break;
            case 5:
                this.goToClaims();
                break;
            case 6:
                this.goToConfig();
                break;
            case 7:
                break;
            case 8:
                this.goToSingleQueryVin();
                break;
            case 9:
                this.goToConsultBySiteMexico();
                break;
            case 10:
                this.goToReports();
                break;
            case 11:
                this.goToExchange();
                break;
            case 12:
                this.goToMultipleVinConsult();
                break;
            case 14:
                this.logout();
                break;
            case 15:
                this.goToMatrizEstatusEquipo();
                break;
            case 16:
                this.goToProgramacionCarga();
                break;
            case 17:
                this.goToAlertasPage();
                break;
            case 20:
                this.goToShipmentRequest();
                break;
            case 21:
                this.goToConsultaDeTractor();
                break;
            case 22:
                this.goToInvestorRelationsPage();
                break;
            case 23:
                this.goToAssetsManagerPage();
                break;
            /* case 24:
                this.goToRateTrip();
                break; */
            case 25:
                this.goToVesselTravelList();
                break;
            case 60:
                this.goToUserPermissions();
                break;
            case 61:
                this.goToAdminPanel();
                break;
            case 100:
                this.goToWebFrame();
                break;
            case 110:
                this.goToiFramePage();
                break;
        }
    }

    getActiveMenuItem(viewInstance: string) {
        // console.log('getActiveMenuItem: ', viewInstance);
        if (viewInstance.includes('/home')) {
            this.activeMenuItem = "home-item";
        }
        if (viewInstance.includes('/dashboard/')) {
            this.activeMenuItem = "dashboard-item";
        }
        if (viewInstance.includes('/dashboard_bsc')) {
            this.activeMenuItem = "dashboard-bsc-item";
        }
        if (viewInstance.includes('/tracking')) {
            this.activeMenuItem = "tracking-map-item";
        }
        if (viewInstance.includes('/travel/list')) {
            // travelType 1 Travel
            // travelType 3 Ratings
            this.activeMenuItem = "travel-list-item";
        }
        if (viewInstance.includes('/travel/list/vessel')) {
            // travelType 1 Travel
            // travelType 3 Ratings
            this.activeMenuItem = "vessel-travel-list-item";
        }
        if (viewInstance.includes('/exchange')) {
            this.activeMenuItem = "exchange-item";
        }
        if (viewInstance.includes('/shipment-request')) {
            this.activeMenuItem = "shipment-request-item";
        }
        if (viewInstance.includes('/report-menu')) {
            this.activeMenuItem = "resport-list-item";
        }
        if (viewInstance.includes('/consulta/by_site_mexico')) {
            this.activeMenuItem = "consulta-cd-item";
        }
        if (viewInstance.includes('/vehicle/multiple')) {
            this.activeMenuItem = "consulta-vin-item";
        }
        if (viewInstance.includes('/equipment')) {
            this.activeMenuItem = "consulta-tractor-item";
        }
        if (viewInstance.includes('/equipment/site')) {
            this.activeMenuItem = "matriz-estatus-item";
        }
        if (viewInstance.includes('/equipment/schedule')) {
            this.activeMenuItem = "programacion-carga-item";
        }
        if (viewInstance.includes('/alerts')) {
            this.activeMenuItem = "alarmas-item";
        }
        if (viewInstance.includes('/config')) {
            this.activeMenuItem = "configuracion-item";
        }
        if (viewInstance.includes('/rate-trip')) {
            this.activeMenuItem = "rate-trip-item";
        }
        if (viewInstance.includes('/investor-relations')) {
            this.activeMenuItem = "investor-relations-item";
        }
        if (viewInstance.includes('/assets-manager')) {
            this.activeMenuItem = "assets-manager-item";
        }
        if (viewInstance.includes('/permissions')) {
            this.activeMenuItem = "permisos-item";
        }
    }

    goToHome(dashboard: any = null) {
        let body = null;
        if (dashboard) {
            this.router.navigate(['/home/' + dashboard], { replaceUrl: true });
        } else {
            this.router.navigate(['/home'], { replaceUrl: true });
        }
    }

    goToDashboardFinanciero() {
        this.router.navigate(['/dashboard/financiero'], { replaceUrl: true });
    }

    goToDashboardOperativo() {
        this.router.navigate(['/dashboard/operativo'], { replaceUrl: true });
    }

    goToDashboardAMDF() {
        this.router.navigate(['/dashboard/amdf'], { replaceUrl: true });
    }

    goToDashboardMazda() {
        this.router.navigate(['/dashboard/mazda'], { replaceUrl: true });
    }

    goToDashboardBMW() {
        this.router.navigate(['/dashboard/bmw'], { replaceUrl: true });
    }

    goToClaims() {
        this.router.navigate(['/claims'], { replaceUrl: true });
    }

    goToConfig() {
        this.router.navigate(['/config'], { replaceUrl: true });
    }

    goToUserPermissions() {
        this.router.navigate(['/permissions'], { replaceUrl: true });
    }

    goToAdminPanel() {
        this.router.navigate(["/admin-panel"], { replaceUrl: true });
    }

    goToWebFrame() {
        this.router.navigate(['/quantum/fleet'], { replaceUrl: true });
    }

    goToVesselTravelList() {
        const routeParams = { // Se genera un objeto con los parametros a transmitir a la siguiente route
            routeId: 'transit', // 1.-En Transito - (transit), 2.-Calificado - (scored), 3.-Entregado - (delivered)
            travelType: 1,
        };

        // this.dataService.setData(routeParams.routeId, routeParams); // Con ayuda del provider DataService asignamos el objeto parametros a un iD, para de esta manera poder recibirlo en la siguiente ruta/pantalla
        this.router.navigate(['/travel/list/vessel'], { replaceUrl: true });
    }

    goToTravelList() {
        const routeParams = { // Se genera un objeto con los parametros a transmitir a la siguiente route
            routeId: 'transit', // 1.-En Transito - (transit), 2.-Calificado - (scored), 3.-Entregado - (delivered)
            travelType: 1,
        };

        this.dataService.setData(routeParams.routeId, routeParams); // Con ayuda del provider DataService asignamos el objeto parametros a un iD, para de esta manera poder recibirlo en la siguiente ruta/pantalla
        this.router.navigate(['/travel/list/' + routeParams.routeId], { replaceUrl: true });
    }

    goToTrackingMap() {
        this.router.navigate(['/tracking'], { replaceUrl: true });
    }

    goToScore() {
        const routeParams = { // Se genera un objeto con los parametros a transmitir a la siguiente route
            routeId: 'delivered', // 1.-En Transito - (transit), 2.-Calificado - (scored), 3.-Entregado - (delivered)
            travelType: 3,
        };

        this.dataService.setData(routeParams.routeId, routeParams); // Con ayuda del provider DataService asignamos el objeto parametros a un iD, para de esta manera poder recibirlo en la siguiente ruta/pantalla
        this.router.navigate(['/travel/list/' + routeParams.routeId], { replaceUrl: true });
    }

    goToReports() {
        this.router.navigate(['/report/menu'], { replaceUrl: true });
    }

    goToSingleQueryVin() {
        this.router.navigate(['/vehicle/single'], { replaceUrl: true });
    }

    goToMultipleVinConsult() {
        this.router.navigate(['/vehicle/multiple'], { replaceUrl: true });
    }

    goToConsultBySiteMexico() {
        this.router.navigate(['/consulta/by_site_mexico'], { replaceUrl: true });
    }

    goToExchange() {
        this.router.navigate(['/exchange'], { replaceUrl: true });
    }

    goToMatrizEstatusEquipo() {
        this.router.navigate(['/equipment/site'], { replaceUrl: true });
    }

    goToProgramacionCarga() {
        this.router.navigate(['/equipment/schedule'], { replaceUrl: true });
    }

    goToAlertasPage() {
        this.router.navigate(['/alerts'], { replaceUrl: true });
    }

    goToShipmentRequest() {
        this.router.navigate(['/shipment-request'], { replaceUrl: true });
    }

    goToConsultaDeTractor() {
        this.router.navigate(['/equipment'], { replaceUrl: true });
    }

    goToInvestorRelationsPage() {
        this.router.navigate(['/investor-relations'], { replaceUrl: true });
    }

    goToAssetsManagerPage() {
        this.router.navigate(['/assets-manager'], { replaceUrl: true });
    }

    goToDashboardBSCCPVPage() {
        this.router.navigate(['/dashboard_bsc/cpv'], { replaceUrl: true });
    }

    goToDashboardBSCCSIPage() {
        this.router.navigate(['/dashboard_bsc/csi'], { replaceUrl: true });
    }

    goToDashboardBSCCSIDPage() {
        this.router.navigate(['/dashboard_bsc/csid'], { replaceUrl: true });
    }

    goToDashboardBSCCSIXPage() {
        this.router.navigate(['/dashboard_bsc/csix'], { replaceUrl: true });
    }

    goToDashboardBSCSunusPage() {
        this.router.navigate(['/dashboard_bsc/sunus'], { replaceUrl: true });
    }

    goToRateTrip() {
        this.router.navigate(['/rate-trip'], { replaceUrl: true });
    }

    /**
     * @name updateAppRequired
     * @desc Callback para mostrar una alerta solicitando que es necesario actualizar la app porque se cuenta con una version no soportada.
     */
    private async updateAppRequired() {
        const titulosAlerta = this.translate.instant('Etiqueta._Actualizar_APP');
        const mensajeAlerta = this.translate.instant('Mensaje._Actualizar_APP', { storeName: this.appStoreName });
        const aceptarButonAlerta = this.translate.instant('Boton._Actualizar_APP', { storeName: this.appStoreName });

        this.versionCheckServ.alertUpdateAppRequired(titulosAlerta, mensajeAlerta, aceptarButonAlerta);
    }

    goToiFramePage() {
        console.log("FRAME OPEN");

        this.storage.get('Quantum:externalURL').then((item: any) => {

            const routeParams = { // Se genera un objeto con los parametros a transmitir a la siguiente route
                externalURL: item.externalURL, // URL del item a mostrar en el frame
                routeId: item.routeId,
            };

            this.dataService.setData(routeParams.routeId, routeParams); // Con ayuda del provider DataService asignamos el objeto parametros a un iD, para de esta manera poder recibirlo en la siguiente ruta/pantalla
            this.storage.put(routeParams.routeId, routeParams); // Con ayuda del provider StorageService asignamos el objeto parametros a un iD, para de esta manera poder obtenerlo desde el storage en caso de necesitarlo en la siguiente ruta/pantalla

            this.router.navigate(['/quantum/civar/' + routeParams.routeId], { replaceUrl: true });
        });

    }

    async logout() {
        const isQuantumLogin = await this.storage.get<boolean>('QuantumLogin:isActive');
        console.log('isQuantumLogin: ', isQuantumLogin);

        if (isQuantumLogin) {
            const logoutConfirmed = await this.loginProvider.promptLogout();
            if (logoutConfirmed) {
                this.unloadQuantronWidget();
                this.sessionProvider.resetAll();
                this.goToLoginPage();
            }
        } else {
            const logoutURI = await this.storage.get<string>('QuantumLogin:URLLogout');
            console.log('QuantumLogin:URLLogout = ', logoutURI);

            if (CapacitorUtilsService.isApp) {
                this.capacitorService.goToLaunchBrowser(logoutURI);
            } else {
                window.location.replace(logoutURI);
            }
        }
    }
}
